// eslint-disable-next-line no-unused-vars
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { assets } from "../assets/assets";
const NotFound = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center relative overflow-hidden font-sans selection:bg-primary selection:text-white transition-colors duration-300 ${
        isDarkMode ? "bg-dark-background" : "bg-white"
      }`}
    >
      {/* Background Texture */}
      <div
        className={`absolute inset-0 z-0 opacity-20 pointer-events-none mix-blend-overlay ${
          isDarkMode ? "opacity-30" : "opacity-10"
        }`}
        style={{
          backgroundImage: isDarkMode
            ? `url("https://www.transparenttextures.com/patterns/asphalt-dark.png")`
            : `url("https://www.transparenttextures.com/patterns/asphalt.png")`,
          backgroundSize: "200px",
        }}
      ></div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-6xl px-4">
        {/* Top Typography */}
        <div className="text-center mb-10 md:mb-16 animate-in slide-in-from-top-10 fade-in duration-700">
          <h1
            className={`text-5xl md:text-7xl font-bold tracking-tight mb-4 drop-shadow-lg ${
              isDarkMode ? "text-dark-text" : "text-text"
            }`}
          >
            Page Not Found
          </h1>
          <p
            className={`text-lg md:text-xl max-w-lg mx-auto font-light leading-relaxed ${
              isDarkMode ? "text-dark-muted" : "text-gray-600"
            }`}
          >
            The page you are looking for might have been removed, had its name
            changed, or is temporarily unavailable.
          </p>
        </div>

        {/* The "404" Visual Composition */}
        <div className="flex items-center justify-center gap-4 md:gap-12 relative mb-16 select-none">
          {/* Left 4 */}
          <div className="relative group">
            <span
              className="text-[12rem] md:text-[22rem] font-bold leading-none text-primary opacity-90 transition-transform duration-500 group-hover:-translate-y-2 relative z-10"
              style={{
                textShadow: isDarkMode
                  ? "4px 4px 0px rgba(0,0,0,0.5), 0px 20px 40px rgba(206, 37, 122, 0.3)"
                  : "2px 2px 0px rgba(0,0,0,0.1), 0px 10px 20px rgba(206, 37, 122, 0.2)",
              }}
            >
              4
            </span>
            {/* Worn texture on the number */}
            <div
              className={`absolute inset-0 pointer-events-none z-20 ${
                isDarkMode
                  ? "opacity-20 mix-blend-overlay"
                  : "opacity-10 mix-blend-multiply"
              }`}
              style={{
                backgroundImage: `url("https://www.transparenttextures.com/patterns/cracked-concrete.png")`,
              }}
            ></div>
          </div>

          {/* Center "0" - Caregiver Visual */}
          <div className="relative w-48 h-48 md:w-80 md:h-80 rounded-full shrink-0 animate-in zoom-in fade-in duration-1000 delay-150 group cursor-default">
            {/* Oval Shape Container */}
            <div
              className={`w-full h-full rounded-full overflow-hidden border-4 shadow-2xl relative z-20 ring-1 group-hover:scale-105 transition-all duration-500 ease-out ${
                isDarkMode
                  ? "border-dark-border bg-dark-surface ring-white/10"
                  : "border-gray-200 bg-card ring-gray-200"
              }`}
            >
              <img
                src={assets.not_found}
                alt="Mogzit In Home Care Plc"
                className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500 scale-110"
              />
              {/* Inner Shadow for depth */}
              <div
                className={`absolute inset-0 pointer-events-none ${
                  isDarkMode
                    ? "bg-gradient-to-tr from-black/40 via-transparent to-white/10"
                    : "bg-gradient-to-tr from-black/20 via-transparent to-white/20"
                }`}
              ></div>
            </div>

            {/* Ground Shadow */}
            <div
              className={`absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-8 blur-xl rounded-full z-0 ${
                isDarkMode ? "bg-black/60" : "bg-gray-900/30"
              }`}
            ></div>

            <div className="absolute -top-6 -right-6 w-12 h-12 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute -bottom-2 -left-4 w-8 h-8 bg-primary/10 rounded-full blur-lg animate-pulse delay-700"></div>
          </div>

          {/* Right 4 */}
          <div className="relative group">
            <span
              className="text-[12rem] md:text-[22rem] font-bold leading-none text-primary opacity-90 transition-transform duration-500 group-hover:-translate-y-2 relative z-10"
              style={{
                textShadow: isDarkMode
                  ? "4px 4px 0px rgba(0,0,0,0.5), 0px 20px 40px rgba(206, 37, 122, 0.3)"
                  : "2px 2px 0px rgba(0,0,0,0.1), 0px 10px 20px rgba(206, 37, 122, 0.2)",
              }}
            >
              4
            </span>
            <div
              className={`absolute inset-0 pointer-events-none z-20 ${
                isDarkMode
                  ? "opacity-20 mix-blend-overlay"
                  : "opacity-10 mix-blend-multiply"
              }`}
              style={{
                backgroundImage: `url("https://www.transparenttextures.com/patterns/cracked-concrete.png")`,
              }}
            ></div>
          </div>

          {/* Directional Arrow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-12 md:-translate-y-20 opacity-0 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500 fill-mode-forwards">
            <svg
              width="60"
              height="60"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className={`rotate-12 ${
                isDarkMode ? "text-dark-muted" : "text-gray-400"
              }`}
            >
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="relative z-30 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300 flex flex-col items-center gap-6">
          <button
            onClick={() => navigate(-1)}
            className="group relative px-8 py-4 text-sm tracking-wide transition-all hover:scale-105 flex items-center gap-3 overflow-hidden rounded-xl font-bold"
            style={{
              background: isDarkMode
                ? "linear-gradient(135deg, #CE257A 0%, #A01E63 100%)"
                : "linear-gradient(135deg, #CE257A 0%, #D84591 100%)",
              color: "white",
              boxShadow: isDarkMode
                ? "0 4px 20px rgba(206, 37, 122, 0.3)"
                : "0 4px 20px rgba(206, 37, 122, 0.2)",
            }}
          >
            <span className="relative z-10 flex items-center gap-2">
              <ArrowUpRight size={18} /> GO BACK
            </span>
            {/* Button Hover Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
          </button>
        </div>

        {/* Additional Info */}
        <div
          className={`mt-12 max-w-xl text-center ${
            isDarkMode ? "text-dark-muted" : "text-gray-500"
          }`}
        >
          <p className="text-sm">
            If you believe this is an error, please contact our support team or
            check the URL for typos.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
